import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PackageBuyPage } from './PackageBuy';
import { BackChevron } from '../components/BackChevron';
import {
  ApiError,
  createOrder,
  getProduct,
  getPricingQuote,
  getTicketTypes,
  listSchedules,
  type CreateOrderRequest,
  type ScheduleSummary,
  type SellableTicketType,
} from '../lib/api-client';
import { formatDateTime, formatPrice, pickI18n } from '../lib/format';
import { useI18n } from '../lib/i18n-context';
import { ticketLabel } from '../lib/ticket-label';

/** 產生 Idempotency-Key（POST /orders 必帶）。 */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // 退場路徑：時間 + random（dev only；prod 走原生 randomUUID）
  return `pwa-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 班次可售判斷：未取消 + 剩餘座位 > 0 */
function isSchedulePurchasable(s: ScheduleSummary): boolean {
  return !s.weather_cancelled && s.capacity - s.reserved_count > 0;
}

/**
 * 購買頁。
 *
 * - 列商品之班次（GET /schedules?product_id=）
 * - 使用者選 1 個班次 + 數量
 * - 「下單」→ POST /orders（帶 Idempotency-Key）→ 導向 /orders/:id/checkout
 */
/**
 * 單一班次購買（trip / experience / charter）。套票走 PackageBuyPage —— 它不建班次，
 * 顧客要為每一段各挑一個時段，這裡的單選班次模型表達不了。
 */
function SingleScheduleBuyPage(): JSX.Element {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  /** 各票種數量（key=ticket_type）。 */
  const [counts, setCounts] = useState<Record<string, number>>({});

  const productQuery = useQuery({
    queryKey: ['product', productId],
    queryFn: () => {
      if (productId === undefined) throw new Error('missing product id');
      return getProduct(productId);
    },
    enabled: productId !== undefined,
  });

  const schedulesQuery = useQuery({
    queryKey: ['schedules', productId],
    queryFn: () => {
      if (productId === undefined) throw new Error('missing product id');
      return listSchedules(productId);
    },
    enabled: productId !== undefined,
    staleTime: 30_000,
  });

  // 可售票種 + 各票種單價（依選定班次；web 通路）。多票種票價來源。
  const ticketTypesQuery = useQuery({
    queryKey: ['ticket-types', productId, selectedScheduleId],
    queryFn: () =>
      getTicketTypes({
        product_id: productId as string,
        schedule_id: selectedScheduleId as string,
        channel: 'web',
      }),
    enabled: productId !== undefined && selectedScheduleId !== null,
    staleTime: 30_000,
  });
  const ticketTypes = useMemo<SellableTicketType[]>(
    () => ticketTypesQuery.data?.ticket_types ?? [],
    [ticketTypesQuery.data],
  );

  // 票種載入後預設第一種（adult）數量 1；切換班次重置。
  useEffect(() => {
    setCounts({});
  }, [selectedScheduleId]);
  useEffect(() => {
    if (ticketTypes.length > 0) {
      setCounts((c) => (Object.keys(c).length === 0 ? { [ticketTypes[0]!.ticket_type]: 1 } : c));
    }
  }, [ticketTypes]);

  const tickets = useMemo(
    () =>
      ticketTypes
        .map((tt) => ({ ticket_type: tt.ticket_type, quantity: counts[tt.ticket_type] ?? 0 }))
        .filter((x) => x.quantity > 0),
    [ticketTypes, counts],
  );
  const totalQty = tickets.reduce((s, t) => s + t.quantity, 0);

  // 折後報價（依各票種數量）；與下單 OrdersService 同源 PricingResolver。
  const quoteQuery = useQuery({
    queryKey: ['pricing.quote', productId, selectedScheduleId, JSON.stringify(tickets)],
    queryFn: () =>
      getPricingQuote({
        product_id: productQuery.data!.id,
        schedule_id: selectedScheduleId as string,
        channel: 'web',
        tickets,
      }),
    enabled: selectedScheduleId !== null && tickets.length > 0 && productQuery.data !== undefined,
    staleTime: 30_000,
  });
  const subtotal = quoteQuery.data ? Number(quoteQuery.data.total) : 0;
  // 原價總和（劃線價 × 數量）；與折後 subtotal 之差即促銷折抵額。
  const originalTotal = quoteQuery.data
    ? quoteQuery.data.lines.reduce((s, l) => s + Number(l.original_price) * l.quantity, 0)
    : 0;
  const promoSaved = Math.max(0, originalTotal - subtotal);

  const setCount = (tt: string, n: number): void =>
    setCounts((c) => ({ ...c, [tt]: Math.max(0, Math.min(10, n)) }));

  const createOrderMutation = useMutation({
    mutationFn: async () => {
      if (!productQuery.data || selectedScheduleId === null || tickets.length === 0) {
        throw new Error('order payload not ready');
      }
      const items = ticketTypes
        .filter((tt) => (counts[tt.ticket_type] ?? 0) > 0)
        .map((tt) => ({
          product_id: productQuery.data!.id,
          schedule_id: selectedScheduleId,
          quantity: counts[tt.ticket_type]!,
          unit_price: Number(tt.unit_price), // OrderItemDto.unit_price 要 number
          ticket_type: tt.ticket_type,
        }));
      const payload: CreateOrderRequest = {
        channel: 'web',
        subtotal,
        total: subtotal,
        items,
      };
      return createOrder(payload, newIdempotencyKey());
    },
    onSuccess: (order) => {
      navigate(`/orders/${order.id}/checkout`);
    },
  });

  const submitDisabled =
    selectedScheduleId === null || totalQty < 1 || createOrderMutation.isPending;

  return (
    <div className="app-shell">
      <header className="topbar topbar-lead">
        <BackChevron
          to={productId !== undefined ? `/products/${productId}` : '/'}
          label={t('common.back')}
        />
        <h1>{t('buy.title')}</h1>
      </header>

      {productQuery.isSuccess && (
        <p style={{ color: '#555', margin: '4px 0 12px' }}>
          {pickI18n(productQuery.data.name_i18n, productQuery.data.sku, locale)}
        </p>
      )}

      {schedulesQuery.isPending && <p>{t('buy.loadingSchedules')}</p>}
      {schedulesQuery.isError && (
        <p className="error-text">
          {t('buy.scheduleLoadFailed')}
          {schedulesQuery.error instanceof ApiError
            ? (schedulesQuery.error.problem?.detail ?? schedulesQuery.error.message)
            : t('common.unknownError')}
        </p>
      )}
      {schedulesQuery.isSuccess && (
        <section>
          {schedulesQuery.data.items.length === 0 ? (
            <p>{t('buy.noSchedules')}</p>
          ) : (
            schedulesQuery.data.items.map((s) => {
              const remaining = s.capacity - s.reserved_count;
              const disabled = !isSchedulePurchasable(s);
              const selected = selectedScheduleId === s.id;
              return (
                <label
                  key={s.id}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.55 : 1,
                    border: selected ? '2px solid #0E5A8C' : '1px solid transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="schedule"
                    value={s.id}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => setSelectedScheduleId(s.id)}
                    style={{ accentColor: '#0e5a8c', width: 18, height: 18 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{formatDateTime(s.departure_at, locale)}</div>
                    <div style={{ fontSize: 13, color: '#777' }}>
                      {t('buy.until', { time: formatDateTime(s.departure_end_at, locale) })}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: disabled ? 'var(--boat-danger)' : 'var(--boat-success)',
                      }}
                    >
                      {s.weather_cancelled
                        ? t('buy.cancelled')
                        : remaining > 0
                          ? t('buy.remaining', { remaining, capacity: s.capacity })
                          : t('buy.soldOut')}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </section>
      )}

      <section className="card" style={{ marginTop: 8 }}>
        {ticketTypes.map((tt) => (
          <div
            key={tt.ticket_type}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div>
              <span style={{ fontSize: 15, fontWeight: 500 }}>{ticketLabel(tt, locale)}</span>
              {Number(tt.original_price) > Number(tt.unit_price) ? (
                <>
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 13,
                      color: '#999',
                      textDecoration: 'line-through',
                    }}
                  >
                    {formatPrice(
                      Number(tt.original_price),
                      productQuery.data?.currency ?? 'TWD',
                      locale,
                    )}
                  </span>
                  <span style={{ marginLeft: 6, fontSize: 13, fontWeight: 600, color: '#C2410C' }}>
                    {formatPrice(
                      Number(tt.unit_price),
                      productQuery.data?.currency ?? 'TWD',
                      locale,
                    )}
                  </span>
                </>
              ) : (
                <span style={{ marginLeft: 8, fontSize: 13, color: '#777' }}>
                  {formatPrice(Number(tt.unit_price), productQuery.data?.currency ?? 'TWD', locale)}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                className="qty-step"
                aria-label="−"
                disabled={(counts[tt.ticket_type] ?? 0) <= 0}
                onClick={() => setCount(tt.ticket_type, (counts[tt.ticket_type] ?? 0) - 1)}
              >
                −
              </button>
              <span style={{ fontSize: 16, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>
                {counts[tt.ticket_type] ?? 0}
              </span>
              <button
                type="button"
                className="qty-step"
                aria-label="+"
                disabled={(counts[tt.ticket_type] ?? 0) >= 10}
                onClick={() => setCount(tt.ticket_type, (counts[tt.ticket_type] ?? 0) + 1)}
              >
                +
              </button>
            </div>
          </div>
        ))}
        {totalQty > 0 && productQuery.data && (
          <>
            <hr className="divider" />
            {promoSaved > 0 && (
              <>
                <div className="kv" style={{ alignItems: 'center', marginBottom: 4 }}>
                  <span className="kv-label" style={{ color: '#999' }}>
                    {t('buy.originalTotal')}
                  </span>
                  <span style={{ fontSize: 15, color: '#999', textDecoration: 'line-through' }}>
                    {formatPrice(originalTotal, productQuery.data.currency, locale)}
                  </span>
                </div>
                <div className="kv" style={{ alignItems: 'center', marginBottom: 4 }}>
                  <span className="kv-label" style={{ color: '#C2410C' }}>
                    {t('buy.promoSaved')}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#C2410C' }}>
                    −{formatPrice(promoSaved, productQuery.data.currency, locale)}
                  </span>
                </div>
              </>
            )}
            <div className="kv" style={{ alignItems: 'center', marginBottom: 0 }}>
              <span className="kv-label">{t('buy.subtotal')}</span>
              <span style={{ fontSize: 20, fontWeight: 700 }}>
                {formatPrice(subtotal, productQuery.data.currency, locale)}
              </span>
            </div>
          </>
        )}
        {createOrderMutation.isError && (
          <div className="error-text">
            {t('buy.orderFailed')}
            {createOrderMutation.error instanceof ApiError
              ? (createOrderMutation.error.problem?.detail ?? createOrderMutation.error.message)
              : t('common.unknownError')}
          </div>
        )}
        <button
          type="button"
          className="btn-primary btn-block"
          style={{ marginTop: 16 }}
          disabled={submitDisabled}
          onClick={() => createOrderMutation.mutate()}
        >
          {createOrderMutation.isPending ? t('buy.submitting') : t('buy.submit')}
        </button>
      </section>
    </div>
  );
}

/**
 * 依商品類別分流。
 *
 * 套票此前會落到單一班次的購買頁，查 `/schedules?product_id=<套票>` 得到 0 筆——
 * 商品列得出來、點進去卻是一片空白。
 */
export function BuyPage(): JSX.Element {
  const { id: productId } = useParams<{ id: string }>();
  const { t } = useI18n();
  const productQuery = useQuery({
    queryKey: ['product', productId],
    queryFn: () => {
      if (productId === undefined) throw new Error('missing product id');
      return getProduct(productId);
    },
    enabled: productId !== undefined,
  });

  if (productQuery.isPending) return <p style={{ padding: 16 }}>{t('buy.loadingSchedules')}</p>;
  if (productQuery.data?.category === 'package') {
    return <PackageBuyPage product={productQuery.data} />;
  }
  return <SingleScheduleBuyPage />;
}
